import { useState, useCallback } from 'react'
import req from '@sportninja/common/api/request'
import { GameEditData } from './useGameEdit'

export interface UndoEntry {
  id: string
  timestamp: number
  type: 'move' | 'swap' | 'edit'
  description: string
  gameId: string
  secondGameId?: string
  before: GameEditData
  after: GameEditData
  beforeSecond?: GameEditData
  afterSecond?: GameEditData
}

const MAX_UNDO_ENTRIES = 50

export interface UseUndoStackResult {
  canUndo: boolean
  canRedo: boolean
  undoStack: UndoEntry[]
  redoStack: UndoEntry[]
  lastEntry: UndoEntry | undefined
  nextRedoEntry: UndoEntry | undefined
  pushEntry: (entry: UndoEntry) => void
  undo: (requestId: string) => Promise<boolean>
  redo: (requestId: string) => Promise<boolean>
  clear: () => void
}

export const useUndoStack = (onSuccess?: () => void): UseUndoStackResult => {
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([])
  const [redoStack, setRedoStack] = useState<UndoEntry[]>([])

  const pushEntry = useCallback((entry: UndoEntry) => {
    setUndoStack((prev) => {
      const next = [entry, ...prev]
      return next.slice(0, MAX_UNDO_ENTRIES)
    })
    // Clear redo stack when a new action is taken
    setRedoStack([])
  }, [])

  const undo = useCallback(
    async (requestId: string): Promise<boolean> => {
      const entry = undoStack[0]
      if (!entry) {
        return false
      }

      try {
        if (entry.type === 'swap' && entry.secondGameId) {
          // Re-swap to revert
          await req(`/scheduler/requests/${requestId}/games/swap`, {
            method: 'POST',
            body: JSON.stringify({
              game_a_uid: entry.gameId,
              game_b_uid: entry.secondGameId,
            }),
          })
        } else {
          // Revert to before state
          await req(`/scheduler/requests/${requestId}/games/${entry.gameId}`, {
            method: 'PUT',
            body: JSON.stringify(entry.before),
          })
        }

        setUndoStack((prev) => prev.slice(1))
        setRedoStack((prev) => [entry, ...prev])
        onSuccess?.()
        return true
      } catch {
        return false
      }
    },
    [undoStack, onSuccess]
  )

  const redo = useCallback(
    async (requestId: string): Promise<boolean> => {
      const entry = redoStack[0]
      if (!entry) {
        return false
      }

      try {
        if (entry.type === 'swap' && entry.secondGameId) {
          await req(`/scheduler/requests/${requestId}/games/swap`, {
            method: 'POST',
            body: JSON.stringify({
              game_a_uid: entry.gameId,
              game_b_uid: entry.secondGameId,
            }),
          })
        } else {
          await req(`/scheduler/requests/${requestId}/games/${entry.gameId}`, {
            method: 'PUT',
            body: JSON.stringify(entry.after),
          })
        }

        setRedoStack((prev) => prev.slice(1))
        setUndoStack((prev) => [entry, ...prev])
        onSuccess?.()
        return true
      } catch {
        return false
      }
    },
    [redoStack, onSuccess]
  )

  const clear = useCallback(() => {
    setUndoStack([])
    setRedoStack([])
  }, [])

  return {
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undoStack,
    redoStack,
    lastEntry: undoStack[0],
    nextRedoEntry: redoStack[0],
    pushEntry,
    undo,
    redo,
    clear,
  }
}

export default useUndoStack
